import React from 'react';
import PropTypes from 'prop-types';
import ClassicBadge from './ClassicBadge';
import SwimRunBadge from './SwimRunBadge';
import NkBadge from './NkBadge';

const COLUMN_LABELS = {
  plaats: 'Plaats',
  plaatsCategorie: 'Plaats (cat)',
  startnummer: 'Startnr',
  naam: 'Naam',
  partner: 'Partner',
  team: 'Team',
  woonplaats: 'Woonplaats',
  onderdeel: 'Onderdeel',
  categorie: 'Cat.',
  type: 'Serie',
  totaaltijd: 'Eindtijd',
  tijdrit: 'Tijdrit',
  finale: 'Finale',
  categorieDetail: 'CategorieDetail',
  ageGroup: 'AgeGroup',
  // voeg andere kolommen toe indien nodig
};

// Kolommen die we altijd willen verbergen (interne/onbekende velden)
// 'partner' en 'team' worden voor NK-rijen weergegeven in de 'naam' cel
const HIDDEN_COLUMNS = new Set([
  'jaar', 'type', 'mv', 'plaatsmv', 'ronde1', 'ronde2', 'stnr', 
  'categorieDetail', 'ageGroup', 'partner', 'team'
]);

const PREFERRED_COLUMN_ORDER = [
  'plaats',
  'plaatsCategorie',
  'naam',
  'partner',
  'team',
  'woonplaats',
  'onderdeel',
  'categorie',
  'totaaltijd',
  'tijdrit',
  'finale',
  'startnummer',
];

function UitslagenTabel({
  rows = [],
  error = '',
  loading = false,
  originalRows = [],
  searchTerm = '',
}) {
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2em' }}>Gegevens ophalen...</div>;
  }
  if (error) {
    return (
      <div className="error">
        Fout:
        {' '}
        {error}
      </div>
    );
  }
  if (!Array.isArray(rows) || rows.length === 0) {
    if (originalRows && originalRows.length > 0) {
      return <div>Geen uitslagen gevonden voor filter &apos;{searchTerm}&apos;.</div>;
    }
    return <div>Geen uitslag voor dit jaar. Bekijk event samenvatting voor evt. toelichting.</div>;
  }
  // Defensieve check: alleen kolommen bepalen als rows niet leeg is
  const kolommen = rows.length > 0 
    ? Object.keys(rows[0])
      .filter((k) => !HIDDEN_COLUMNS.has(k))
    : [];
  
  const kolommenOrdered = [
    ...PREFERRED_COLUMN_ORDER.filter((k) => kolommen.includes(k)),
    ...kolommen.filter((k) => !PREFERRED_COLUMN_ORDER.includes(k)),
  ];
  
  return (
    <table className="uitslagen-tabel">
      <thead>
        <tr>
          {kolommenOrdered.map((kolom) => <th key={kolom}>{COLUMN_LABELS[kolom] || kolom}</th>)}
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${row.type || 'type'}-${row.startnummer || 'stnr'}-${row.naam || 'naam'}-${index}`}>
            {kolommenOrdered.map((kolom) => {
              if (kolom === 'naam' && row.type === 'nk') {
                const teamNaam = row.team || row.naam || '';
                const deelnemers = [row.naam, row.partner].filter(Boolean).join(' / ');
                return (
                  <td key={kolom}>
                    {teamNaam}
                    {deelnemers && (
                      <details style={{ display: 'inline-block', marginLeft: '0.4em' }}>
                        <summary style={{ cursor: 'pointer', fontSize: '0.85em', color: '#555' }}>deelnemers</summary>
                        <span style={{ fontSize: '0.85em', whiteSpace: 'nowrap' }}>{deelnemers}</span>
                      </details>
                    )}
                  </td>
                );
              }
              return <td key={kolom}>{row[kolom]}</td>;
            })}
            <td style={{ textAlign: 'center', minWidth: 24 }}>
              {row.type === 'enkel' && <ClassicBadge title="Classic – Enkel Rondje" />}
              {row.type === 'dubbel' && <SwimRunBadge title="SwimRun – Dubbel Rondje" />}
              {row.type === 'nk' && <NkBadge title="NK SwimRun – 19,5K Duo" />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

UitslagenTabel.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    startnummer: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    naam: PropTypes.string,
    partner: PropTypes.string,
    team: PropTypes.string,
    woonplaats: PropTypes.string,
    type: PropTypes.string,
    plaats: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  error: PropTypes.string,
  loading: PropTypes.bool,
  originalRows: PropTypes.arrayOf(PropTypes.shape({
    startNr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    naam: PropTypes.string,
    woonplaats: PropTypes.string,
    type: PropTypes.string,
    plts: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  searchTerm: PropTypes.string,
};

export default UitslagenTabel;
